import * as React from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { LibraryItem } from "./external-libraries.types";

const DEFAULT_PLACEHOLDER = "github.com/google/uuid@v1.6.0";
const DEFAULT_HELP =
  "Modules are fetched from their source repository. Enter the import path, optionally pinned with @version.";

interface AddModuleFormProps {
  installed: LibraryItem[];
  adding: boolean;
  placeholder?: string;
  helpText?: string;
  examples?: string[];
  onAdd: (modulePath: string, version: string) => void;
}

/**
 * "new" tab for `module` source mode: no registry to browse, so the user adds a dependency by its
 * import path (the `path@version` form). An intro, the input + Add, and one-click example chips.
 */
const AddModuleForm: React.FC<AddModuleFormProps> = ({ installed, adding, placeholder, helpText, examples, onAdd }) => {
  const [value, setValue] = React.useState<string>("");

  const trimmed: string = value.trim();
  const atIndex: number = trimmed.lastIndexOf("@");
  const modulePath: string = atIndex > 0 ? trimmed.slice(0, atIndex) : trimmed;
  const typedVersion: string = atIndex > 0 ? trimmed.slice(atIndex + 1).trim() : "";

  const installedModule: LibraryItem | undefined = installed.find((lib) => lib.name === modulePath);
  // Same path + same (or unspecified) version → nothing to do. Same path + new version → an update.
  const isAlreadyInstalled: boolean = !!installedModule && (!typedVersion || installedModule.version === typedVersion);
  const isUpdate: boolean = !!installedModule && !!typedVersion && installedModule.version !== typedVersion;
  const canSubmit: boolean = trimmed.length > 0 && !isAlreadyInstalled && !adding;

  const submit = (): void => {
    if (!canSubmit) return;
    onAdd(trimmed, "");
    setValue("");
  };

  return (
    <div className="kuika-external-libraries__module">
      <div className="kuika-external-libraries__module-intro">
        <span className="kuika-external-libraries__module-medallion">
          <KuikaSvg name="package" width={20} height={20} />
        </span>
        <div className="kuika-external-libraries__module-intro-text">
          <KuikaTextMedium className="kuika-external-libraries__module-heading">Add a module</KuikaTextMedium>
          <KuikaSmallText className="kuika-external-libraries__module-note">{helpText ?? DEFAULT_HELP}</KuikaSmallText>
        </div>
      </div>

      <div className="kuika-external-libraries__module-field">
        <KuikaInput
          size="sm"
          className="kuika-external-libraries__module-input"
          placeholder={placeholder ?? DEFAULT_PLACEHOLDER}
          value={value}
          leftIcon={<KuikaSvg name="package" width={16} height={16} />}
          onChange={(_e: React.ChangeEvent<HTMLInputElement>, next: string | number) => setValue(String(next))}
          onPressEnter={submit}
          data-testid="externalLibrariesModulePathInput"
        />
        <KuikaButton
          type="primary"
          size="sm"
          disabled={!canSubmit}
          loading={adding}
          onClick={submit}
          data-testid="externalLibrariesAddModuleButton"
        >
          {isUpdate ? "Update" : "Add"}
        </KuikaButton>
      </div>

      {isAlreadyInstalled && installedModule && (
        <KuikaSmallText className="kuika-external-libraries__module-note kuika-external-libraries__module-note--warn">
          {modulePath} is already added at {installedModule.version || "an unpinned version"}.
        </KuikaSmallText>
      )}

      {examples && examples.length > 0 && (
        <div className="kuika-external-libraries__module-examples">
          <KuikaSmallText className="kuika-external-libraries__module-examples-label">Popular modules</KuikaSmallText>
          <div className="kuika-external-libraries__module-chips">
            {examples.map((example) => (
              <button
                type="button"
                key={example}
                className="kuika-external-libraries__module-chip"
                onClick={() => setValue(example)}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddModuleForm;
